import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import BrandIcon from '../components/BrandIcon';
import { handleTalkToSpecialist } from '../utils/webchat';
import './Marketplace.css';

const API_URL   = process.env.REACT_APP_API_URL  || 'https://api.libbenagents.com';

// Categorias de agente ocultas no site público (agentes de sistema).
const HIDDEN_CATEGORIES = ['interno'];

// Ordem canônica de exibição (espelha AGENT_CATEGORY_ORDER do backend).
const CATEGORY_ORDER = [
  'saude', 'atendimento', 'vendas', 'suporte', 'marketing', 'financeiro',
  'operacoes', 'juridico', 'rh', 'ti', 'produtividade', 'imobiliario',
  'ecommerce', 'pessoal', 'geral',
];
const catRank = (c) => {
  const i = CATEGORY_ORDER.indexOf(c);
  return i === -1 ? CATEGORY_ORDER.length : i;
};

// ── Listagem do marketplace ───────────────────────────────────────────────────
export default function Marketplace() {
  const { t, i18n }         = useTranslation();
  const [agents, setAgents]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/public/agents`)
      .then(r => r.json())
      .then(data => { setAgents(data.agents || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const visible = agents.filter(a => a.enabled !== false && !HIDDEN_CATEGORIES.includes(a.category));
  const categories = [...new Set(visible.map(a => a.category).filter(Boolean))]
    .sort((a, b) => catRank(a) - catRank(b));

  const filtered = visible
    .filter(a => {
      const q = search.toLowerCase();
      const matchSearch = !q || a.name?.toLowerCase().includes(q) || a.role?.toLowerCase().includes(q) || a.description?.toLowerCase().includes(q);
      const matchCat = !category || a.category === category;
      return matchSearch && matchCat;
    })
    .sort((a, b) => catRank(a.category) - catRank(b.category) || (a.name || '').localeCompare(b.name || ''));

  const catLabel = (key) => t(`categories.${key}`, { defaultValue: key });

  return (
    <>
      <Helmet>
        <title>{t('marketplace.pageTitle', { defaultValue: 'Marketplace de Agentes IA' })} | Libben Agents</title>
        <meta name="description" content={t('marketplace.pageDesc', { defaultValue: 'Explore agentes de IA prontos para WhatsApp, email e webchat. Experimente grátis.' })} />
        <meta property="og:title" content={t('marketplace.pageTitle', { defaultValue: 'Marketplace de Agentes IA — Libben Agents' })} />
        <link rel="canonical" href="https://libbenagents.com/agentes" />
        <html lang={i18n.language} />
      </Helmet>

      <div className="mkt-page">
        <div className="mkt-hero">
          <h1>{t('marketplace.heroTitle', { defaultValue: 'Agentes de IA prontos para usar' })}</h1>
          <p>{t('marketplace.heroSubtitle', { defaultValue: 'Escolha um agente, experimente agora e instale em minutos.' })}</p>
        </div>

        <div className="mkt-filters">
          <input
            className="mkt-search"
            placeholder={t('marketplace.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="mkt-cats">
            <button className={`mkt-cat ${!category ? 'active' : ''}`} onClick={() => setCategory('')}>
              {t('marketplace.allCategories')}
            </button>
            {categories.map(c => (
              <button key={c} className={`mkt-cat ${category === c ? 'active' : ''}`} onClick={() => setCategory(c)}>
                {catLabel(c)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mkt-grid">
            {[...Array(6)].map((_, i) => <div key={i} className="mkt-card-skeleton" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mkt-empty">{t('marketplace.noAgentsFound')}</div>
        ) : (
          <div className="mkt-grid">
            {filtered.map(agent => (
              <div key={agent.agentId} className="mkt-card mkt-card-static">
                <div className="mkt-card-icon"><BrandIcon icon={agent.icon || '🤖'} size={40} /></div>
                <div className="mkt-card-body">
                  <div className="mkt-card-cat">{catLabel(agent.category)}</div>
                  <div className="mkt-card-name">{agent.name}</div>
                  <div className="mkt-card-role">{agent.role}</div>
                  <p className="mkt-card-desc">{agent.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mkt-cta-bar">
          <span>{t('marketplace.ctaText')}</span>
          <button type="button" onClick={handleTalkToSpecialist} className="mkt-cta-btn">
            {t('marketplace.ctaBtn')}
          </button>
        </div>
      </div>
    </>
  );
}
